import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

/* ── Gap util ────────────────────────────────────────────────────────────── */
function calcGap(w) {
  if (w <= 1024) return 55;
  if (w >= 1456) return 86;
  return 55 + (86 - 55) * ((w - 1024) / (1456 - 1024));
}

/* ── Default projects ─────────────────────────────────────────────────────── */
const DEFAULT_PROJECTS = [
  {
    name: 'Website Restaurant',
    designation: 'WEBSITE · 2025',
    quote: 'À LAISE BY THE LAKE — website restoran premium dengan desain elegan, sistem menu interaktif, dan galeri visual yang memukau untuk memperkuat brand presense secara online.',
    src: `${process.env.PUBLIC_URL}/img/project/alaisebythelake.webp`,
    href: 'https://alaise.art',
  },
  {
    name: 'Booking System',
    designation: 'FULLSTACK · 2026',
    quote: 'Sistem reservasi & pembayaran online terintegrasi — lengkap dengan manajemen kapasitas, promo kode, laporan keuangan real-time, dan dashboard admin untuk Alaise.',
    src: `${process.env.PUBLIC_URL}/img/project/alaisebooking.webp`,
    href: 'https://alaisebooking.com',
  },
];

/* ── Component ───────────────────────────────────────────────────────────── */
export function ProjectShowcase({ projects = DEFAULT_PROJECTS, autoplay = true }) {
  const [active, setActive] = useState(0);
  const [isDesktop, setDesktop] = useState(false);
  const [hoverP, setHP] = useState(false);
  const [hoverN, setHN] = useState(false);
  const imgRef = useRef(null);
  const [imgW, setImgW] = useState(900);
  const timerRef = useRef(null);
  const total = projects.length;
  const proj = projects[active];

  /* responsive */
  useEffect(() => {
    const check = () => setDesktop(window.innerWidth >= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  /* track image container width */
  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setImgW(el.offsetWidth));
    ro.observe(el);
    setImgW(el.offsetWidth);
    return () => ro.disconnect();
  }, []);

  /* autoplay */
  useEffect(() => {
    if (!autoplay) return;
    timerRef.current = setInterval(() => setActive(p => (p + 1) % total), 5000);
    return () => clearInterval(timerRef.current);
  }, [autoplay, total]);

  /* keyboard */
  useEffect(() => {
    const fn = (e) => {
      if (e.key === 'ArrowLeft') goTo(p => (p - 1 + total) % total);
      if (e.key === 'ArrowRight') goTo(p => (p + 1) % total);
    };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  });

  const goTo = useCallback((updater) => {
    clearInterval(timerRef.current);
    setActive(updater);
  }, []);

  const handlePrev = () => goTo(p => (p - 1 + total) % total);
  const handleNext = () => goTo(p => (p + 1) % total);

  /* per-image 3D style */
  const getImgStyle = (i) => {
    const gap = calcGap(imgW);
    const maxUp = gap * 0.75;
    const isAct = i === active;
    const isLeft = (active - 1 + total) % total === i;
    const isRight = (active + 1) % total === i;
    const base = { transition: 'all 0.75s cubic-bezier(.4,2,.3,1)' };
    if (isAct) return { ...base, zIndex: 3, opacity: 1, pointerEvents: 'auto', transform: 'translateX(0) translateY(0) scale(1) rotateY(0deg)', cursor: 'default' };
    if (isLeft) return { ...base, zIndex: 2, opacity: 0.9, pointerEvents: 'auto', transform: `translateX(-${gap}px) translateY(-${maxUp}px) scale(0.82) rotateY(14deg)`, cursor: 'pointer' };
    if (isRight) return { ...base, zIndex: 2, opacity: 0.9, pointerEvents: 'auto', transform: `translateX(${gap}px) translateY(-${maxUp}px) scale(0.82) rotateY(-14deg)`, cursor: 'pointer' };
    return { ...base, zIndex: 1, opacity: 0, pointerEvents: 'none' };
  };

  return (
    <>
      <style>{`
        .ps-grid {
          display: grid;
          gap: 3rem;
          grid-template-columns: 1fr;
          align-items: center;
        }
        @media (min-width: 768px) {
          .ps-grid {
            grid-template-columns: 1.1fr 0.9fr;
            gap: 4rem;
          }
        }
      `}</style>

      <div className="ps-grid">
        {/* ── Left: Image Stack ── */}
        <div
          ref={imgRef}
          style={{
            position: 'relative',
            width: '100%',
            height: 'clamp(18rem, 40vw, 28rem)',
            perspective: '1000px',
          }}
        >
          {projects.map((p, i) => (
            <img
              key={i}
              src={p.src}
              alt={p.name}
              onClick={() => i !== active && goTo(() => i)}
              style={{
                position: 'absolute',
                width: '100%', height: '100%',
                objectFit: 'cover',
                borderRadius: '1.25rem',
                boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                ...getImgStyle(i),
              }}
            />
          ))}
        </div>

        {/* ── Right: Content ── */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -18 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              {/* Name */}
              <h3 style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: 'clamp(1.4rem, 2.5vw, 2rem)',
                color: '#fff', letterSpacing: '-0.03em', margin: '0 0 0.3rem',
              }}>{proj.name}</h3>

              {/* Designation */}
              <p style={{
                fontSize: '0.6rem', fontFamily: "'Space Mono', monospace",
                letterSpacing: '0.2em', textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.3)', marginBottom: '1.5rem',
              }}>{proj.designation}</p>

              {/* Quote — word blur-in */}
              <p style={{
                fontSize: 'clamp(0.82rem, 1.2vw, 0.92rem)',
                fontFamily: "'Inter', sans-serif",
                color: 'rgba(255,255,255,0.55)', lineHeight: 1.8,
                margin: 0,
              }}>
                {proj.quote.split(' ').map((w, wi) => (
                  <motion.span key={wi}
                    initial={{ filter: 'blur(8px)', opacity: 0, y: 4 }}
                    animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: 'easeInOut', delay: 0.018 * wi }}
                    style={{ display: 'inline-block' }}
                  >
                    {w}&nbsp;
                  </motion.span>
                ))}
              </p>

              {/* CTA */}
              {proj.href && (
                <a href={proj.href} target="_blank" rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    marginTop: '1.75rem',
                    fontSize: '0.58rem', fontFamily: "'Space Mono', monospace",
                    letterSpacing: '0.18em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.65)', textDecoration: 'none',
                    border: '1px solid rgba(255,255,255,0.15)',
                    padding: '7px 16px', borderRadius: '20px',
                    background: 'rgba(255,255,255,0.04)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.18)'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
                >
                  LIHAT PROYEK →
                </a>
              )}
            </motion.div>
          </AnimatePresence>

          {/* ── Navigation ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '2.5rem' }}>
            {/* Arrows */}
            {[{ fn: handlePrev, Icon: FaArrowLeft, h: hoverP, sH: setHP },
            { fn: handleNext, Icon: FaArrowRight, h: hoverN, sH: setHN }]
              .map(({ fn, Icon, h, sH }, idx) => (
                <button key={idx} onClick={fn}
                  onMouseEnter={() => sH(true)} onMouseLeave={() => sH(false)}
                  style={{
                    width: '2.4rem', height: '2.4rem', borderRadius: '50%',
                    border: 'none', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: h ? '#6366f1' : 'rgba(255,255,255,0.08)',
                    transition: 'background 0.25s ease', flexShrink: 0,
                  }}>
                  <Icon size={14} color="#f1f1f7" />
                </button>
              ))}

            {/* Dots */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {projects.map((_, i) => (
                <button key={i} onClick={() => goTo(() => i)}
                  style={{
                    width: i === active ? '22px' : '5px',
                    height: '4px', borderRadius: '3px',
                    border: 'none', cursor: 'pointer', padding: 0,
                    background: i === active ? '#6366f1' : 'rgba(255,255,255,0.18)',
                    transition: 'all 0.35s ease',
                  }}
                />
              ))}
            </div>

            {/* Counter */}
            <span style={{
              marginLeft: 'auto',
              fontSize: '0.6rem', fontFamily: "'Space Mono', monospace",
              color: 'rgba(255,255,255,0.2)', letterSpacing: '0.1em',
            }}>
              <span style={{ color: '#6366f1', fontWeight: 700 }}>0{active + 1}</span> / 0{total}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
